/**
 * 등락(상승/하락/보합) 의미 색 토큰 생성
 *
 * - palette.gain / palette.loss / palette.flat 로 테마에 주입된다 (ThemeContext).
 * - 기본값은 한국식(상승=빨강, 하락=파랑), 개인 설정 trendColorScheme='western' 이면
 *   서구식(상승=초록, 하락=빨강).
 * - 기준 색(hue/saturation)은 고정이고, 테마 변형(8종 x light/dark)의 paper/default 배경에
 *   대해 텍스트 4.5:1 이상이 되도록 lightness 만 보정한다 (라이트=어둡게, 다크=밝게).
 *   -> 테마마다 실제 hex 값은 다르지만 규칙은 한 곳.
 */
import {
  contrastRatio,
  ensureContrast,
  hexToHsl,
  hslToHex,
  normalizeHex,
} from '../utils/colorContrast';

export const TREND_SCHEMES = {
  korean: {
    label: '한국식',
    description: '상승 빨강 / 하락 파랑',
    base: { gain: '#E53935', loss: '#1E88E5' },
  },
  western: {
    label: '글로벌식',
    description: '상승 초록 / 하락 빨강',
    base: { gain: '#2E9E4F', loss: '#E53935' },
  },
};

export const DEFAULT_TREND_SCHEME = 'korean';

export const normalizeTrendScheme = (scheme) => (
  Object.prototype.hasOwnProperty.call(TREND_SCHEMES, scheme) ? scheme : DEFAULT_TREND_SCHEME
);

/** 텍스트용 최소 대비비 (WCAG AA) */
export const TREND_MIN_CONTRAST = 4.5;

const shiftLightness = (hex, delta) => {
  const hsl = hexToHsl(hex);
  return hslToHex({ ...hsl, l: Math.min(0.95, Math.max(0.05, hsl.l + delta)) });
};

const contrastTextOf = (hex) => (
  contrastRatio(hex, '#000000') >= contrastRatio(hex, '#FFFFFF') ? '#000000' : '#FFFFFF'
);

const makeToken = (main) => ({
  main,
  light: shiftLightness(main, 0.12),
  dark: shiftLightness(main, -0.1),
  contrastText: contrastTextOf(main),
});

/**
 * @param {object} args
 * @param {'korean'|'western'} args.scheme
 * @param {'light'|'dark'} args.mode
 * @param {string} args.paper        theme.palette.background.paper
 * @param {string} [args.background] theme.palette.background.default
 * @param {string} [args.textSecondary] flat 색의 기준(없으면 모드별 회색)
 * @returns {{gain: object, loss: object, flat: object}}
 */
export const buildTrendPalette = ({
  scheme = DEFAULT_TREND_SCHEME,
  mode = 'light',
  paper = '#FFFFFF',
  background,
  textSecondary,
} = {}) => {
  const base = TREND_SCHEMES[normalizeTrendScheme(scheme)].base;
  const direction = mode === 'dark' ? 'lighten' : 'darken';
  const bgs = [normalizeHex(paper) || '#FFFFFF', normalizeHex(background)].filter(Boolean);
  const flatBase = normalizeHex(textSecondary) || (mode === 'dark' ? '#B0B0B0' : '#6B6B6B');

  return {
    gain: makeToken(ensureContrast(base.gain, bgs, TREND_MIN_CONTRAST, direction)),
    loss: makeToken(ensureContrast(base.loss, bgs, TREND_MIN_CONTRAST, direction)),
    flat: makeToken(ensureContrast(flatBase, bgs, TREND_MIN_CONTRAST, direction)),
  };
};

export default buildTrendPalette;
